import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Wrench, CalendarClock, Plus } from "lucide-react";
import camApi from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";
import { PickerSelect } from "@/components/cam/AssetDrawer";

const REPAIR_STATUS = { open: { label: "Open", color: "#e6a700" }, in_progress: { label: "In progress", color: "#0654FC" }, completed: { label: "Completed", color: "#16a34a" } };

export default function Maintenance() {
  const { can } = useCam();
  return (
    <Tabs defaultValue="orders">
      <TabsList>
        <TabsTrigger value="orders"><Wrench className="h-3.5 w-3.5 mr-1.5" />Work orders</TabsTrigger>
        <TabsTrigger value="schedules"><CalendarClock className="h-3.5 w-3.5 mr-1.5" />Preventive schedules</TabsTrigger>
      </TabsList>
      <TabsContent value="orders" className="mt-4"><WorkOrders canWrite={can.admin || can.maintenance} /></TabsContent>
      <TabsContent value="schedules" className="mt-4"><Schedules canWrite={can.admin || can.maintenance} /></TabsContent>
    </Tabs>
  );
}

function WorkOrders({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const load = async () => { setLoading(true); try { const r = await camApi.repairs(); setRows(r.repairs || []); } catch (e) { toast.error(e.message); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  return (
    <div className="glass rounded-2xl overflow-hidden">
      {loading ? <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? <div className="p-10 text-center text-sm text-muted-foreground">No work orders. Raise one from an asset's detail panel.</div>
          : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                <th className="px-4 py-2.5">Asset</th><th className="px-4 py-2.5">Complaint</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 hidden sm:table-cell">Tech</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const m = REPAIR_STATUS[r.status] || {};
                  return (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-black/[0.02] cursor-pointer" onClick={() => canWrite && setActive(r)}>
                      <td className="px-4 py-2.5"><div className="font-medium">{r.asset?.name}</div><div className="text-mono text-[11px] text-muted-foreground">{r.asset?.asset_tag}</div></td>
                      <td className="px-4 py-2.5 max-w-[280px] truncate text-muted-foreground">{r.complaint}</td>
                      <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1.5 text-xs font-semibold"><span className="h-2 w-2 rounded-full" style={{ background: m.color }} />{m.label || r.status}</span></td>
                      <td className="px-4 py-2.5 hidden sm:table-cell text-muted-foreground">{r.tech?.name || "—"}</td>
                      <td className="px-4 py-2.5 text-right text-xs text-cobalt-500">{canWrite && r.status !== "completed" ? "Update" : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      {active && <RepairDialog repair={active} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} />}
    </div>
  );
}

function RepairDialog({ repair, onClose, onSaved }) {
  const [f, setF] = useState({ status: repair.status, diagnosis: repair.diagnosis || "", root_cause: repair.root_cause || "", parts_replaced: repair.parts_replaced || "", repair_cost: repair.repair_cost || "", resolution: repair.resolution || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    setBusy(true);
    try {
      await camApi.updateRepair(repair.id, { ...f, repair_cost: f.repair_cost === "" ? null : Number(f.repair_cost) });
      toast.success(f.status === "completed" ? "Work order completed" : "Updated"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display tracking-tighter">Work order — {repair.asset?.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">{repair.complaint}</p>
        <div className="space-y-3">
          <div><Label className="text-xs">Status</Label><PickerSelect value={f.status} onChange={set("status")} options={Object.entries(REPAIR_STATUS).map(([k, v]) => ({ value: k, label: v.label }))} className="mt-1" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Diagnosis</Label><Input className="mt-1" value={f.diagnosis} onChange={set("diagnosis")} /></div>
            <div><Label className="text-xs">Root cause</Label><Input className="mt-1" value={f.root_cause} onChange={set("root_cause")} /></div>
            <div><Label className="text-xs">Parts replaced</Label><Input className="mt-1" value={f.parts_replaced} onChange={set("parts_replaced")} /></div>
            <div><Label className="text-xs">Repair cost (₹)</Label><Input type="number" className="mt-1" value={f.repair_cost} onChange={set("repair_cost")} /></div>
          </div>
          <div><Label className="text-xs">Resolution</Label><Textarea className="mt-1" rows={2} value={f.resolution} onChange={set("resolution")} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Schedules({ canWrite }) {
  const { categories } = useCam();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = async () => { setLoading(true); try { const r = await camApi.maintenance(); setRows(r.schedules || []); } catch (e) { toast.error(e.message); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const remove = async (id) => { if (!window.confirm("Remove this schedule?")) return; try { await camApi.removeSchedule(id); toast.success("Removed"); load(); } catch (e) { toast.error(e.message); } };

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-cobalt-500" /><div className="overline">Preventive maintenance</div></div>
        {canWrite && <Button size="sm" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New schedule</Button>}
      </div>
      {loading ? <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No preventive schedules yet.</div>
          : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Every</th><th className="px-4 py-2.5">Next due</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map((s) => {
                  const overdue = s.next_due_at && s.next_due_at < new Date().toISOString().slice(0, 10);
                  return (
                    <tr key={s.id} className="border-b last:border-0">
                      <td className="px-4 py-2.5 font-medium">{s.name}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{s.interval_months} mo</td>
                      <td className="px-4 py-2.5"><span className={overdue ? "text-red-500 font-medium" : "text-muted-foreground"}>{s.next_due_at ? new Date(s.next_due_at).toLocaleDateString() : "—"}{overdue ? " · overdue" : ""}</span></td>
                      <td className="px-4 py-2.5 text-right">{canWrite && <button onClick={() => remove(s.id)} className="text-xs text-muted-foreground hover:text-red-500">Remove</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      {open && <ScheduleDialog categories={categories} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function ScheduleDialog({ categories, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", interval_months: 6, target: "category", category_id: "", asset_id: "", next_due_at: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required."); return; }
    if (f.target === "category" && !f.category_id) { toast.error("Pick a category."); return; }
    setBusy(true);
    try {
      await camApi.createSchedule({
        name: f.name.trim(), interval_months: Number(f.interval_months), next_due_at: f.next_due_at || null,
        category_id: f.target === "category" ? Number(f.category_id) : null,
        asset_id: f.target === "asset" && f.asset_id ? Number(f.asset_id) : null,
      });
      toast.success("Schedule added"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tighter">New maintenance schedule</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Quarterly projector service" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Interval (months)</Label><Input type="number" min={1} className="mt-1" value={f.interval_months} onChange={(e) => setF({ ...f, interval_months: e.target.value })} /></div>
            <div><Label className="text-xs">Next due</Label><Input type="date" className="mt-1" value={f.next_due_at} onChange={(e) => setF({ ...f, next_due_at: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs">Applies to a category</Label>
            <PickerSelect placeholder="Select category" value={f.category_id} onChange={(v) => setF({ ...f, target: "category", category_id: v })} options={categories.map((c) => ({ value: String(c.id), label: c.name }))} className="mt-1" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={save} disabled={busy}>{busy ? "Saving…" : "Add"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
